import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { PrimaryButton, SecondaryButton } from "@/components/ui-kit";
import { X, RefreshCw, Download, FileText, Cloud, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { sanitizeHtml } from "@/lib/sanitize";

const REPORT_STYLES = `
  .aa-doc { font-family: Arial, sans-serif; color: #222; line-height: 1.55; font-size: 13px; padding: 20px; background: white; }
  .aa-doc h1, .aa-doc h3 { font-family: 'Cabinet Grotesk', sans-serif; letter-spacing: .3px; }
  .aa-doc h3 { font-size: 14px; margin-top: 18px; margin-bottom: 8px; text-transform: uppercase; border-left: 3px solid #FF5A00; padding-left: 8px; }
  .aa-doc ul { padding-left: 20px; }
  .aa-doc li { margin-bottom: 4px; }
  .aa-doc table { font-size: 13px; }
`;

export default function ReportModal({ serviceId, customerName, onClose }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/services/${serviceId}/report`);
    setReport(data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [serviceId]);

  const regenerate = async () => {
    setLoading(true);
    const { data } = await api.post(`/services/${serviceId}/report/regenerate`);
    setReport({ html: data.html, generated: true });
    setLoading(false);
    toast.success("Informe regenerado");
  };

  const download = async () => {
    try {
      const res = await fetch(`${API_BASE}/services/${serviceId}/report.pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("Error PDF");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Informe_${customerName.replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Error generando PDF");
    }
  };

  const uploadToDrive = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/services/${serviceId}/report/upload-drive`);
      toast.success("Informe subido a Drive");
      if (data.webViewLink) window.open(data.webViewLink, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error subiendo a Drive");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="report-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-4xl border-t-4 border-[#FF5A00] max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Informe de mantenimiento</div>
            <div className="font-display font-bold text-xl">{customerName}</div>
          </div>
          <button onClick={onClose} className="hover:bg-neutral-100 p-2"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-6 py-3 border-b border-neutral-200 bg-neutral-50 flex items-center justify-end gap-2">
          <SecondaryButton onClick={regenerate} disabled={loading} data-testid="report-regenerate">
            <RefreshCw className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Regenerar
          </SecondaryButton>
          {report && (
            <>
              <SecondaryButton onClick={download} data-testid="report-download">
                <Download className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> PDF
              </SecondaryButton>
              <PrimaryButton onClick={uploadToDrive} disabled={loading} data-testid="report-upload-drive">
                <Cloud className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Subir a Drive
              </PrimaryButton>
            </>
          )}
        </div>

        <style>{REPORT_STYLES}</style>
        <div className="flex-1 overflow-auto bg-neutral-100 p-6" data-testid="report-body">
          {loading || !report ? (
            <div className="text-center py-16">
              <FileText className="w-6 h-6 text-neutral-400 animate-pulse mx-auto" />
            </div>
          ) : (
            <div className="bg-white shadow-sm max-w-3xl mx-auto" dangerouslySetInnerHTML={{ __html: sanitizeHtml(report.html) }} />
          )}
        </div>
      </div>
    </div>
  );
}
